import { Injectable } from '@angular/core';
import { HttpService } from '@app/@core/services/http.service';
import { Observable } from 'rxjs';
import { InAppMessageConfig } from './in-app-message-config.config';
import {
  InAppMessage,
  CreateMessageRequest,
  LookupItem,
  LookupResponse,
  MarkAsReadRequest,
  MessageQueryParams,
  PaginatedResponse,
  Recipient,
  UpdateMessageRequest,
  AssociatedUser
} from './in-app-message.types';

@Injectable({
  providedIn: 'root'
})
export class InAppMessageService {
  constructor(private httpService: HttpService) { }

  
  /**
   * Get users associated with applications using the senior's query structure
   * @param entity_Id - The application ID
   * @param entity_Type 
   * @returns Observable of users with detailed information
   */
  getassociatedusersForCase(entity_Type: string, entity_Id: number,): Observable<any[]> {
    console.log(InAppMessageConfig)
    const endpoint = InAppMessageConfig.getassociatedusersForCase(entity_Type, entity_Id);
     return this.httpService.get(endpoint) as Observable<any[]>;
  }

  /**
   * Convert AssociatedUser to Recipient format for UI components
   * @param users - Array of AssociatedUser objects
   * @returns Array of Recipient objects
   */
  convertToRecipients(users: any[]): Recipient[] {
    return users.map((user) => {
       
      // Handle different user object structures
      const id = user.id || user.userId || user.user_id;
      const fullName = user.fullName || user.full_name || user.name || `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim();
      const role = user.role || user.userRole || user.user_role || 'User';
      const email = user.email || user.emailAddress || user.email_address || null;
      
      return {
        id: id?.toString() || Math.random().toString(),
        name: fullName || 'Unknown User',
        role: role,
        role_id: user.role_id != null ? Number(user.role_id) : undefined,
        email: email,
        avatar: null,
        initials: user.initials || this.getInitials(fullName),
        is_application_creator: !!user.is_application_creator
      };
    });
  }

  /**
   * Generate initials from full name
   * @param fullName - The full name
   * @returns Initials string
   */
  private getInitials(fullName: string): string {
    if (!fullName) return '';
    
    const names = fullName.split(' ');
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase();
    } else if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    return '';
  }

  /**
   * Load case users with fallback strategy
   * @param entity_Id - Primary application ID to try
   * @param entity_Type - Fallback applicant ID
   * @returns Observable of Recipient objects
   */
  loadCaseUsersWithFallback(entity_Type: string, entity_Id: number,): Observable<Recipient[]> {
    return new Observable(observer => {
       
      // Try application-based query first
      this.getassociatedusersForCase(entity_Type, entity_Id).subscribe({
        next: (response: any) => {
           
          // Handle different response formats
          let users: any[] = [];
          
          // If response has a data property, use that
          if (response && response.data && Array.isArray(response.data)) {
            users = response.data;
          }
          // If response is an array, use it directly
          else if (Array.isArray(response)) {
            users = response;
          }
          // If response has users property, use that
          else if (response && response.users && Array.isArray(response.users)) {
            users = response.users;
          }
          
           
          if (users && users.length > 0) {
            const recipients = this.convertToRecipients(users);
             observer.next(recipients);
            observer.complete();
          } else {
             observer.next([]);
            observer.complete();
          }
        },
        error: (error) => {
           observer.error(error);
        }
      });
    });
  }

  // ==================== MESSAGE API METHODS ====================

  /**
   * Send a new message
   * @param messageData - Message data to send
   * @returns Observable of created message
   */
  sendMessage(messageData: CreateMessageRequest): Observable<InAppMessage> {
    const endpoint = '/solution/in-app-message/message/send';
 
    return this.httpService.post(endpoint, messageData) as Observable<InAppMessage>;
  }

  /**
   * Get inbox messages
   * @param params - Query parameters
   * @returns Observable of paginated inbox messages
   */
  getInboxMessages(params: MessageQueryParams = {}): Observable<PaginatedResponse<InAppMessage>> {
    const queryString = this.buildQueryString(params);
    const endpoint = `/solution/in-app-message/messages/inbox${queryString}`;
     return this.httpService.get(endpoint) as Observable<PaginatedResponse<InAppMessage>>;
  }

  /**
   * Get sent messages
  //  * @param params - Query parameters
  //  * @returns Observable of paginated sent messages
  //  */
  // getSentMessages(params: MessageQueryParams = {}): Observable<PaginatedResponse<InAppMessage>> {
  //   const queryString = this.buildQueryString(params);
  //   const endpoint = `/solution/in-app-message/messages/inbox${queryString}`;
  //   return this.httpService.get(endpoint) as Observable<PaginatedResponse<InAppMessage>>;
  // }

  /**
  //  * Get sent messages using POST with body parameters
  //  * @param params - Message query parameters in body
  //  * @returns Observable of paginated sent messages
  //  */
  // // getSentMessagesByBody(params: MessageQueryParams = {}): Observable<PaginatedResponse<InAppMessage>> {
  // //   const endpoint = '/case-inbox/messages/sent';
  // //   return this.httpService.post(endpoint, params) as Observable<PaginatedResponse<InAppMessage>>;
  // // }

  /**
   * Get message recipients by message ID
   * @param messageId - Message ID
   * @returns Observable of recipients
   */
  getMessageRecipients(messageId: number): Observable<any[]> {
    const endpoint = `/solution/in-app-message/messages/${messageId}/recipients`;
    return this.httpService.get(endpoint) as Observable<any[]>;
  }

  /**
   * Get draft messages
  //  * @param params - Query parameters
  //  * @returns Observable of paginated draft messages
  //  */
  // getDraftMessages(params: MessageQueryParams = {}): Observable<PaginatedResponse<InAppMessage>> {
  //   const queryString = this.buildQueryString(params);
  //   console.log('params in draft api',params)
  //   console.log('messageView value:', params.messageView);
  //   console.log('messageView type:', typeof params.messageView);
  //   const endpoint = `/solution/in-app-message/messages/inbox${queryString}`;
  //   return this.httpService.get(endpoint) as Observable<PaginatedResponse<InAppMessage>>;
  // }

  /**
  //  * Get draft messages using POST with body parameters
  //  * @param params - Message query parameters in body
  //  * @returns Observable of paginated draft messages
  //  */
  // getDraftMessagesByBody(params: MessageQueryParams = {}): Observable<PaginatedResponse<InAppMessage>> {
  //   const endpoint = '/case-inbox/messages/draft';
  //   return this.httpService.post(endpoint, params) as Observable<PaginatedResponse<InAppMessage>>;
  // }

  /**
   * Get archived messages
  //  * @param params - Query parameters
  //  * @returns Observable of paginated archived messages
  //  */
  // getArchivedMessages(params: MessageQueryParams = {}): Observable<PaginatedResponse<InAppMessage>> {
  //   const queryString = this.buildQueryString(params);
  //   const endpoint = `/solution/in-app-message/messages/inbox${queryString}`;
  //   return this.httpService.get(endpoint) as Observable<PaginatedResponse<InAppMessage>>;
  // }

  /**
   * Get archived messages using POST with body parameters
   * @param params - Message query parameters in body
   * @returns Observable of paginated archived messages
   */
  getArchivedMessagesByBody(params: MessageQueryParams = {}): Observable<PaginatedResponse<InAppMessage>> {
    const endpoint = '/case-inbox/messages/archive';
    return this.httpService.post(endpoint, params) as Observable<PaginatedResponse<InAppMessage>>;
  }

  /**
   * Get all messages
   * @param params - Query parameters
   * @returns Observable of paginated messages
   */
  getAllMessages(params: MessageQueryParams = {}): Observable<PaginatedResponse<InAppMessage>> {
    const queryString = this.buildQueryString(params);
    const endpoint = `/case-inbox/messages${queryString}`;
    return this.httpService.get(endpoint) as Observable<PaginatedResponse<InAppMessage>>;
  }

  /**
   * Build query string from MessageQueryParams
   * @param params - MessageQueryParams object
   * @returns Query string
   */
  private buildQueryString(params: MessageQueryParams): string {
    const queryParams: string[] = [];
    
    if (params.page) queryParams.push(`page=${params.page}`);
    if (params.limit) queryParams.push(`limit=${params.limit}`);
    if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
    if (params.applicationId) queryParams.push(`applicationId=${params.applicationId}`);
    if (params.caseId) queryParams.push(`caseId=${params.caseId}`);
    if (params.userId) queryParams.push(`userId=${params.userId}`);
    if (params.messageType) queryParams.push(`messageType=${params.messageType}`);
    if (params.isRead !== undefined) queryParams.push(`isRead=${params.isRead}`);
    if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
    if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
    if (params.entityId) queryParams.push(`entityId=${params.entityId}`);
    if (params.messageView !== undefined && params.messageView !== null) {
      queryParams.push(`messageView=${encodeURIComponent(params.messageView.trim())}`);
    }
    
    return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  }

  /**
   * Get message by ID
   * @param messageId - Message ID
   * @returns Observable of message details
   */
  getMessageById(messageId: number): Observable<InAppMessage> {
    const endpoint = `/solution/in-app-message/message/${messageId}`;
    return this.httpService.get(endpoint) as Observable<InAppMessage>;
  }

  /**
   * Update message
   * @param messageId - Message ID
   * @param updateData - Update data
   * @returns Observable of updated message
   */
  updateMessage(messageId: number, updateData: UpdateMessageRequest): Observable<InAppMessage> {
    const endpoint = `/solution/in-app-message/message/${messageId}`;
    return this.httpService.put(endpoint, updateData) as Observable<InAppMessage>;
  }

  /**
   * Mark message as read
   * @param messageId - Message ID
   * @param recipientId - Recipient User ID (current user)
   * @returns Observable of updated message
   */
  markAsRead(messageId: number, recipientId: number): Observable<InAppMessage> {
    const endpoint = `/solution/in-app-message/message/${messageId}/read`;
    const updateData: MarkAsReadRequest = {
      messageId: messageId,
      recipientId: recipientId,
      isRead: true,
      readAt: new Date().toISOString()
    };
     return this.httpService.put(endpoint, updateData) as Observable<InAppMessage>;
  }

  

  /**
   * Delete message
   * @param messageId - Message ID
   * @returns Observable of deletion result
   */
  deleteMessage(messageId: number): Observable<{ message: string }> {
    const endpoint = `/case-inbox/message/${messageId}`;
    return this.httpService.delete(endpoint) as Observable<{ message: string }>;
  }


  /**
   * Convert message to display format for UI
   * @param message - Message object from API
   * @returns Formatted message for display
   */
  formatMessageForDisplay(message: InAppMessage): any {
    return {
      id: message.id,
      subject: message.subject,
      content: message.messageContent,
      sender: message.sender ? `${message.sender.firstname} ${message.sender.lastname}` : 'Unknown',
      recipient: message.recipients && message.recipients.length > 0 ? message.recipients.map(r => r.name).join(', ') : 'Unknown',
      isRead: message.isRead || false,
      readAt: message.readAt,
      priority: message.priority,
      date: new Date(message.insertedOn),
      attachments: [], // TODO: Implement attachments later
      messageType: message.messageType
    };
  }

  /**
   * Get lookup data for message types and priorities
   * @returns Observable of lookup data
   */
  getLookupData(): Observable<LookupResponse> {
    const endpoint = '/case-inbox/lookup';
    return this.httpService.get(endpoint) as Observable<LookupResponse>;
  }

  /**
   * Get message types from lookup table
   * @returns Observable of message type options
   */
  getMessageTypes(): Observable<LookupItem[]> {
    const endpoint = '/case-inbox/lookup/message-types';
    return this.httpService.get(endpoint) as Observable<LookupItem[]>;
  }

  /**
   * Get priorities from lookup table
   * @returns Observable of priority options
   */
  getPriorities(): Observable<any[]> {
    const endpoint = '/solution/in-app-message/lookup/priorities';
    return this.httpService.get(endpoint) as Observable<any[]>;
  }

  /**
   * Archive a message
   * @param messageId - Message ID to archive
   * @param userId - User ID who is archiving the message
   * @returns Observable of archived message
   */
  archiveMessage(messageId: number, userId?: number): Observable<InAppMessage> {
    const endpoint = `/solution/in-app-message/message/${messageId}/archive`;
    const body = userId ? { userId } : {};
    return this.httpService.put(endpoint, body) as Observable<InAppMessage>;
  }

  /**
   * Get unread inbox message count for a specific application and user
   * @param entityId - Entity ID (application id)
   * @param userId - User ID (optional)
   * @returns Observable of unread count
   */
  getUnreadCount(entityId: number, userId?: number): Observable<{ count: number }> {
    const endpoint = InAppMessageConfig.EndPoint.Message.getUnreadCount;
    const payload = { entityId, userId };
    return this.httpService.post(endpoint, payload) as Observable<{ count: number }>;
  }

  /**
   * Get unread message counts for multiple entities (applications) at once.
   * Used for badge / total unread across all applications.
   */
  getUnreadCountsForMultipleEntities(
    entityIds: number[],
    userId?: number
  ): Observable<{ data: Array<{ message_entity_id: number; count: number }> }> {
    const endpoint = InAppMessageConfig.EndPoint.Message.getUnreadCountsAll;
    const payload = { entityIds, userId };
    return this.httpService.post(endpoint, payload) as Observable<{
      data: Array<{ message_entity_id: number; count: number }>;
    }>;
  }

  softDeleteMessage(messageId: number): Observable<InAppMessage> {
    const endpoint = `/solution/in-app-message/message/draft/${messageId}`;
    return this.httpService.delete(endpoint) as Observable<InAppMessage>;
  }

  unArchiveMessage(messageId: number, userId?: number): Observable<InAppMessage> {
    const endpoint = `/solution/in-app-message/message/${messageId}/unarchive`;
    const body = userId ? { userId } : {};
    return this.httpService.put(endpoint, body) as Observable<InAppMessage>;
  }

} 